<template>
  <div>
    <a-upload
      :before-upload="beforeUpload"
      :custom-request="customRequest"
      :disabled="disabled"
      :fileList="fileList"
      :multiple="true"
      @remove="onRemove"
      accept=".jpg,.png,.jpeg"
      class="hmd-btn"
      list-type="picture-card"
      name="file"
      @preview="onPreview"
    >
      <a-spin v-if="!disabled && fileList.length < maxCount" :spinning="uploading">
        <ant-icon type="PlusOutlined"></ant-icon>
        <div class="ant-upload-text">{{ buttonText }}</div>
      </a-spin>
    </a-upload>

    <div class="text-grey">{{ tip }}</div>

    <a-modal :footer="null" :visible="previewVisible" @cancel="closePreviewModal">
      <img :src="previewUrl" style="width: 100%" />
    </a-modal>
    <img-cropper v-if="cropperVisible" :image="cropperUrl" :cropperInfo="cropperInfo" @closeCropper="closeCropper" @saveCropper="saveCropper"></img-cropper>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, reactive, toRefs, watch } from "vue";
import { message,Form } from "ant-design-vue";
import { FileUploadService } from "@/core";
import { generateUuid, getFileExt, getFileName } from "@/utils";
import { AnyTypeAnnotation } from "@babel/types";

interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  url?: string;
}

interface IState {
  uploading: boolean;
  previewVisible: Boolean;
  previewUrl: string;
  fileList: FileItem[];
  fileUrls: ComputedRef<string[]>;
  cropperVisible: Boolean;
  cropperUrl: string;
}

/**
 * 照片墙上传组件
 */
export default defineComponent({
  isGlobal: true,
  name: "PictureCardUpload",
  props: {
    // maxCount 为 1 时, value 为 string
    // maxCount > 1 时, value 为 string[]
    value: { type: [String, Array], default: "" },

    // 是否禁用
    disabled: { type: Boolean, default: false },

    // 单文件最大大小, 单位 M
    maxSize: { type: Number, default: 100 },

    // 文件个数
    maxCount: { type: Number, default: Infinity },

    // 按钮文案
    buttonText: { type: String, default: "点击上传" },

    // 提示文案
    tip: { type: String, default: "" },

    // 裁剪模式  只支持单个
    cropper: { type: Boolean, default: false },

    // 裁剪信息  裁剪模式下有效  
    cropperInfo: { type: Object, default: { width: 320,height: 200,} },
  },
  emits: ["update:value", "change"],
  setup(props, { emit }) {
    const formItemContext = Form.useInjectFormItemContext();
    const state = reactive<IState>({
      uploading: false,
      fileList: [] as any,
      fileUrls: computed(() => {
        return (state.fileList || []).map((item) => item.url);
      }),
      previewVisible: false,
      previewUrl: "",
      cropperVisible: false,
      cropperUrl: "",
    });

    const maxSizeByte = computed(() => {
      return props.maxSize * 1024 * 1024;
    });

    const beforeUpload = (file, fileList) => {
      const ext = getFileExt(file.name);
      if (!["jpg", "png", "jpeg"].includes(ext)) {
         message.destroy();
        message.warning("仅支持jpg、jpeg、png格式");
        return false;
      }
      if (file.size > maxSizeByte.value) {
         message.destroy();
        message.warning(`单个文件不得超过${props.maxSize}M`);
        return false;
      }
      if (fileList.length + state.fileList.length > props.maxCount) {
         message.destroy();
        message.warning(`上传文件已超过最大数量限制`);
        return false;
      }
    };

    const customRequest = async ({ file }) => {
      state.uploading = true;
      const url = await FileUploadService.uploadToOss(file);
      if (props?.cropper && props?.maxCount === 1) {
        cropper(url)
        return
      }
      state.fileList.push({
        uid: generateUuid(),
        name: getFileName(url),
        status: "done",
        url,
      });
      emit("update:value", state.fileUrls);
      state.uploading = false;
    };

    const cropper = (url)=> {
      state.cropperVisible = true;
      state.cropperUrl = url
    }

    const closeCropper = () => {
      state.cropperVisible = false;
      state.cropperUrl = '';
      state.uploading = false;
    }

    const saveCropper = async (file) => {
      const url = await FileUploadService.uploadToOss(file);
      // console.log(url)
      state.fileList.push({
        uid: generateUuid(),
        name: getFileName(url),
        status: "done",
        url,
      });
      emit("update:value", state.fileUrls);
      closeCropper()
      state.uploading = false;
    }


    const onRemove = ({ url }) => {
      state.fileList = state.fileList.filter((item) => item.url !== url);
      emit("update:value", state.fileUrls);
    };

    const onPreview = ({ url }) => {
      state.previewUrl = url;
      state.previewVisible = true;
    };

    const closePreviewModal = () => {
      state.previewVisible = false;
      state.previewUrl = "";
    };

    watch(
      () => props.value,
      (urls: string[]) => {
        if (Array.isArray(urls)) {
          state.fileList = urls.reduce((pre, url) => {
            pre.push({
              uid: generateUuid(),
              name: getFileName(url),
              status: "done",
              url,
            });
            return pre;
          }, [] as any);
        } else {
          state.fileList = [];
        }
        formItemContext.onFieldChange();
      },
      { immediate: true, deep: true }
    );

    return {
      ...toRefs(state),
      beforeUpload,
      customRequest,
      onRemove,
      onPreview,
      closePreviewModal,
      closeCropper,
      saveCropper,
    };
  },
});
</script>
